import React, { useEffect } from 'react';
import { func, string, bool, shape } from 'prop-types';
import { useAmountPurpose } from '@shared_modules/hooks';
import { Page } from '../../components';
import { monthlyPaymentExperimentVariants } from '@shared_modules/hooks/AmountPurpose/constants';
import { heapTrackEvent } from '@shared_modules/baui-heap-tracking';
import {
  isMonthlyPaymentType,
  verifyAmountAndPurposeFieldsFromURL,
  loanAmountFlatValidation,
  getMonthlyPaymentTrackingProps,
  getInputsTrackingProps,
} from '@shared_modules/hooks/AmountPurpose/utils';
import { getBtMktLoanPurposeTrackingAttrs } from '../../utils/util';

const PageTypes = {
  DEFAULT: 'DEFAULT',
  ONLY_MONTHLY_PAYMENT: 'ONLY_MONTHLY_PAYMENT',
  CASH: 'CASH',
  BT: 'BT',
};

const defaultProps = {
  title: 'How much are you looking for?',
  header: "We'll try to match your financial needs as closely as possible.",
};

const pageVariants = {
  [PageTypes.DEFAULT]: {
    ...defaultProps,
  },
  [PageTypes.ONLY_MONTHLY_PAYMENT]: {
    title: 'How much would you like your monthly payment to be?',
    header: "We'll do our best to give you options within your budget.",
  },
  [PageTypes.CASH]: {
    ...defaultProps,
  },
  [PageTypes.BT]: {
    ...defaultProps,
    title: 'How much debt do you want to pay down?',
    header: 'You can include credit card debt, personal loan balances, and any extra cash you may need.',
  },
};

const calculatePageVariant = ({ loanAmount, loanPurpose, monthlyPaymentExperiment, btVariant }) => {
  if (['A', 'B'].includes(monthlyPaymentExperiment) && loanAmount && loanPurpose) {
    return PageTypes.ONLY_MONTHLY_PAYMENT;
  }
  return btVariant ? PageTypes[btVariant.toUpperCase()] : PageTypes.DEFAULT;
};

const AmountPurpose = ({
  loanAmount: initialLoanAmount,
  loanPurpose: initialLoanPurpose,
  monthlyPayment: initialMonthlyPayment,
  dm,
  saveValues,
  nextLocation,
  previousLocation,
  experiments: { PL_PI1_MONTHLY_PAYMENT: monthlyPaymentExperiment } = {},
  loanIntentSubmitted,
  isLoanAmountPrefill,
  isLoanPurposePrefill,
  btVariant,
}) => {
  const monthlyPaymentExperimentVariant = monthlyPaymentExperimentVariants[monthlyPaymentExperiment];
  const isMonthlyPaymentVariant = isMonthlyPaymentType(monthlyPaymentExperimentVariant);
  const isMonthlyPaymentExp = Object.keys(monthlyPaymentExperimentVariants).includes(monthlyPaymentExperiment);

  const {
    loanAmount,
    loanPurpose,
    monthlyPayment,
    onChange,
    onBlur,
    validationErrors,
    createSubmitHandler,
    AmountPurposeFields,
  } = useAmountPurpose(initialLoanAmount, initialLoanPurpose, initialMonthlyPayment, {
    variant: monthlyPaymentExperimentVariant,
  });

  const wasPrefillField = (value, previousState) => (loanIntentSubmitted ? previousState : !!value);

  const wasLoanAmountPrefill = wasPrefillField(initialLoanAmount, isLoanAmountPrefill);
  const wasLoanPurposePrefill = wasPrefillField(initialLoanPurpose, isLoanPurposePrefill);
  const isValidInitialLoanAmount = Object.keys(loanAmountFlatValidation(initialLoanAmount)).length === 0;
  const isValidLoanAmountAndLoanPurpose = isValidInitialLoanAmount && !!initialLoanPurpose;
  const hasValidInitialUrlValues = verifyAmountAndPurposeFieldsFromURL();

  const showLoanAmount =
    !isMonthlyPaymentVariant ||
    (isMonthlyPaymentVariant && (!wasLoanAmountPrefill || !hasValidInitialUrlValues)) ||
    (wasLoanAmountPrefill && !isValidLoanAmountAndLoanPurpose);
  const showLoanPurpose =
    !isMonthlyPaymentVariant ||
    (isMonthlyPaymentVariant && (!wasLoanPurposePrefill || !hasValidInitialUrlValues)) ||
    (wasLoanPurposePrefill && !isValidLoanAmountAndLoanPurpose);

  const getMonthlyPaymentAttrs = () =>
    getMonthlyPaymentTrackingProps({
      loanAppData: { loanAmount, loanPurpose, monthlyPayment },
    });

  const getInputsTrackingAttrs = () =>
    getInputsTrackingProps({
      showMonthlyPaymentInput: isMonthlyPaymentVariant,
      loanAmountVisible: showLoanAmount,
      loanPurposeVisible: showLoanPurpose,
    });

  useEffect(() => {
    heapTrackEvent('Loan_Intent_Page_Render', {
      BORROWER_TYPE: 'New',
      PRODUCT_TYPE: 'PL',
      ...(isMonthlyPaymentExp && getMonthlyPaymentAttrs()),
      ...getInputsTrackingAttrs(),
      ...getBtMktLoanPurposeTrackingAttrs({ withType: true }),
    });
  }, []);

  const submit = () => {
    heapTrackEvent('Loan_Intent_Page_Submit', {
      BORROWER_TYPE: 'New',
      PRODUCT_TYPE: 'PL',
      ...(isMonthlyPaymentExp && getMonthlyPaymentAttrs()),
      ...getInputsTrackingAttrs(),
      ...getBtMktLoanPurposeTrackingAttrs({ withType: true }),
      LOAN_PURPOSE: loanPurpose,
    });

    saveValues({
      ...(isMonthlyPaymentVariant && {
        loanIntentSubmitted: true,
        isLoanAmountPrefill: wasLoanAmountPrefill,
        isLoanPurposePrefill: wasLoanPurposePrefill,
      }),
      prequal: {
        loanAmount,
        loanPurpose,
        btVariant,
        ...(isMonthlyPaymentVariant && { monthlyPayment }),
      },
    });
    nextLocation();
  };

  const { title, header } =
    pageVariants[
      calculatePageVariant({
        loanAmount: initialLoanAmount,
        loanPurpose: initialLoanPurpose,
        monthlyPaymentExperiment,
        btVariant,
      })
    ];

  return (
    <Page
      title={title}
      header={header}
      name="amount-purpose"
      goBack={previousLocation}
      submitHandler={createSubmitHandler(submit)}
    >
      <AmountPurposeFields
        loanAmount={loanAmount}
        loanPurpose={loanPurpose}
        monthlyPayment={monthlyPayment}
        handleInputChange={onChange}
        handleBlur={onBlur}
        validationErrors={validationErrors}
        dm={dm}
        variant={monthlyPaymentExperimentVariant}
        showLoanAmount={showLoanAmount}
        showLoanPurpose={showLoanPurpose}
        btVariantType={btVariant}
      />
    </Page>
  );
};

AmountPurpose.propTypes = {
  loanAmount: string,
  loanPurpose: string,
  saveValues: func.isRequired,
  previousLocation: func.isRequired,
  nextLocation: func.isRequired,
  dm: shape({
    isDm: bool,
    isBtaDmReferrer: bool,
  }),
};

AmountPurpose.defaultProps = {
  loanAmount: '',
  loanPurpose: '',
  dm: {
    isDm: false,
    isBtaDmReferrer: false,
  },
};

export default AmountPurpose;

